import React from 'react';
import { Control, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TDR } from 'tdr-common';
import AgreementCheckbox from '../agreement-checkbox/AgreementCheckbox';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';
import { GuestInfo } from './types';

interface MarketingPreferencesProps {
	restaurant: TDR.Restaurant;
	control: Control<GuestInfo>;
	register: UseFormRegister<GuestInfo>;
	watch: UseFormWatch<GuestInfo>;
	setValue: UseFormSetValue<GuestInfo>;
}

export const MarketingPreferences: React.FC<MarketingPreferencesProps> = ({
	restaurant,
	register,
	watch,
	setValue
}) => {
	const { t, i18n } = useTranslation();

	const customDisclaimer = restaurant.settings?.checkout?.marketingConsentDisclaimer;
	const isCombined = restaurant.settings?.checkout?.combinedMarketingConsent || false;

	return (
		<div className="flex flex-col gap-4">
			{/* Marketing Preferences Section */}
			<div>
				<h3 className="text-sm font-semibold text-zinc-300 mb-3">
					{t('consent.marketing_preferences', { defaultValue: 'Marketing preferences' })}
				</h3>
				<div className="flex flex-col gap-3">
					{isCombined
						? (
							<AgreementCheckbox
								id='marketingConsent'
								checked={watch('emailMarketingConsentGranted') || false}
								onChange={(evt) => {
									const checked = evt.target.checked;
									setValue('emailMarketingConsentGranted', checked);
									setValue('smsMarketingConsentGranted', checked);
								}}
								label={t('consent.combined_marketing', {
									restaurantName: restaurant.displayName,
									defaultValue: `Receive news, offers, and updates from ${restaurant.displayName} via email and text`
								})}
								labelVariant='small'
							/>
						)
						: (
							<>
								<AgreementCheckbox
									id='emailMarketingConsent'
									checked={watch('emailMarketingConsentGranted') || false}
									{...register('emailMarketingConsentGranted')}
									label={t('consent.email_marketing', {
										restaurantName: restaurant.displayName,
										defaultValue: `Receive news and offers via email from ${restaurant.displayName}`
									})}
									labelVariant='small'
								/>

								<AgreementCheckbox
									id='smsMarketingConsent'
									checked={watch('smsMarketingConsentGranted') || false}
									{...register('smsMarketingConsentGranted')}
									label={t('consent.sms_marketing', {
										restaurantName: restaurant.displayName,
										defaultValue: `Receive promotional texts from ${restaurant.displayName}`
									})}
									labelVariant='small'
								/>
							</>
						)}
				</div>
			</div>

			{/* SMS Disclaimer */}
			<p className="text-xs text-zinc-400">
				{t('consent.sms_disclaimer', {
					defaultValue: 'Reply STOP to unsubscribe. Message frequency varies. Message & data rates may apply.'
				})}
			</p>

			{/* Optional Restaurant-Specific Marketing Consent Disclaimer */}
			{customDisclaimer && (
				<p className="text-xs text-zinc-400">
					<ReactMarkdown
						remarkPlugins={[remarkGfm]}
						components={{
							p: ({ children }) => <>{children}</>,
							a: ({ href, children }) => (
								<a href={href} target="_blank" rel="noopener noreferrer" className="underline">
									{children}
								</a>
							)
						}}
					>
						{resolveLocalizedText(customDisclaimer, i18n.language)}
					</ReactMarkdown>
				</p>
			)}
		</div>
	);
};
